import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { formatMomentDate } from "../../../utils/dateHelper";
import { useSelector, useDispatch } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { useFilterDrower } from "../../../context/FilterDrowerContext";
import InputField from "../../../components/form/InputField";
import { changeUserStatus, fetchAppUserStats, exportAllUsers, fetchUserDetail, updateUserDetails, addUser, deleteAppUser, fetchProfessions, changeUserPassword } from "../../../redux/slice/allUsersSlice";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { RecordIcon, Upload04Icon, VerifiedIcon } from "../../../assets/icons";
import { Calendar, Clock, EyeOff, Plus, Star } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import StatusChip from "../../../components/statusChip/StatusChip";
import StatusChangeModal from "./StatusChangeModal";
import CommonButton from "../../../components/commonButton";
import EditProfileModal from "../../../components/modals/UserProfile/EditProfileModal";
import DeletePopup from "../../../components/delete-popup";
import { usePermission } from "../../../hooks/usePermission";
import AppUserFilterDrawer from "./AppUserFilterDrawer";
import PasswordChangeModal from "../../../components/modals/PasswordChangeModal";
import { useToast } from "../../../context/TostContext";
import CustomTable from "../../../components/table/CustomTable";
import { FilterBtn, ResetBtn } from "../../../components/commonButton/btns";
import { TableSearch } from "../../../components/form/Fields";
import { UserInital } from "../../../utils/otherHelopers";
import CustomSelect from "../../../components/form/CustomSelect";
import { CheckboxOption, FormLabel, MultiValueMessage } from "../../../components/form/utils";
import CustomDateRange from "../../../components/form/CustomDateRange";


const customStyles = {
  menu: (provided) => ({
    ...provided,
    minWidth: "250px",
    zIndex: 9999
  }),
  multiValueRemove: () => ({
    display: 'none',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    backgroundColor: state.isSelected
      ? "transparent"
      : state.isFocused
        ? "var(--primary-100)"
        : "transparent",
    color: 'var(-text-primary)',
    ':active': {
      backgroundColor: "var(--primary-100)"
    }
  })
};

const ListSection = ({
  data,
  isLoading,
  isInitialLoad,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  cardStatus,
  selectedCities,
  setSelectedCities,
  selectedProfessions,
  setSelectedProfessions,
  selectedPlans = [],
  setSelectedPlans,
  advancedFilters,
  setAdvancedFilters,

  planOptions = [],
  limit,
  handleChange,
}) => {
  const { hasPermission } = usePermission();
  const toast = useToast();
  const MODULE = "user_management";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUserLoading, userDetail } = useSelector((state) => state.allUsers);

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedCities?.length > 0) count += 1;
    if (selectedProfessions?.length > 0) count += 1;
    if (selectedPlans?.length > 0) count += 1;

    if (advancedFilters?.min_connections || advancedFilters?.max_connections) count += 1;
    if (advancedFilters?.min_followers || advancedFilters?.max_followers) count += 1;
    if (advancedFilters?.min_companies || advancedFilters?.max_companies) count += 1;
    if (advancedFilters?.min_posts || advancedFilters?.max_posts) count += 1;
    if (advancedFilters?.company_verified_status) count += 1;
    if (advancedFilters?.verification_status) count += 1;
    if (
      advancedFilters?.inactive_days ||
      advancedFilters?.active_today ||
      advancedFilters?.active_last_7_days ||
      advancedFilters?.never_logged_in ||
      advancedFilters?.last_login_start ||
      advancedFilters?.last_login_end
    ) count += 1;
    if (advancedFilters?.start_date || advancedFilters?.end_date) count += 1;

    return count;
  };

  const activeFilterCount = getActiveFilterCount();
  const isFilterActive = activeFilterCount > 0;
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForStatus, setSelectedUserForStatus] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'edit' | 'add' | null
  const [editLoadingId, setEditLoadingId] = useState(null);

  const [inlineVerification, setInlineVerification] = useState("all");
  const [createdDateRange, setCreatedDateRange] = useState({ start: null, end: null });
  const [lastLoginRange, setLastLoginRange] = useState({ start: null, end: null });

  const { openFilterDrower, closeFilterDrower } = useFilterDrower();
  const { cities = [] } = useSelector((state) => state.location || {});
  const { professions = [] } = useSelector((state) => state.allUsers || {});

  const cityOptions = cities.map(c => ({ value: c.id, label: c.name }));
  const professionOptions = professions.map(p => ({ value: p.id, label: p.name }));

  const mergedCityOptions = [
    ...(selectedCities || []),
    ...cityOptions.filter(c => !(selectedCities || []).some(sc => sc.value === c.value))
  ];

  const mergedProfessionOptions = [
    ...(selectedProfessions || []),
    ...professionOptions.filter(p => !(selectedProfessions || []).some(sp => sp.value === p.value))
  ];

  const citySearchTimeoutRef = useRef(null);
  const professionSearchTimeoutRef = useRef(null);

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
      citySearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchActiveCities({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchActiveCities({ limit: 20 }));
    }
  };

  const handleProfessionSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (professionSearchTimeoutRef.current) clearTimeout(professionSearchTimeoutRef.current);
      professionSearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchProfessions({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchProfessions({ limit: 20 }));
    }
  };

  const filterStateRef = useRef(advancedFilters || {});

  const handleResetFilters = () => {
    setInlineVerification("all");
    setCreatedDateRange({ start: null, end: null });
    setLastLoginRange({ start: null, end: null });
    setSearchTerm("");
    setAdvancedFilters({});
    setSelectedCities([]);
    setSelectedProfessions([]);
    setSelectedPlans([]);
    setCurrentPage(1);
  };

  const handleOpenFilterDrawer = () => {
    const content = (
      <AppUserFilterDrawer
        initialFilters={advancedFilters || {}}
        onChange={(newFilters) => {
          filterStateRef.current = newFilters;
        }}
      />
    );

    openFilterDrower(content, {
      title: "Filters",
      onClear: () => {
        setAdvancedFilters({});
        setSelectedCities([]);
        setSelectedProfessions([]);
        setSelectedPlans([]);
        setInlineVerification("all");
        setCreatedDateRange({ start: null, end: null });
        setLastLoginRange({ start: null, end: null });
        setCurrentPage(1);
        closeFilterDrower();
      },
      onSubmit: () => {
        setAdvancedFilters(filterStateRef.current);

        // Also update selectedCities/Professions/Plans for backward compatibility in index.js display
        const newCities = cityOptions.filter(c => filterStateRef.current.city_id?.includes(c.value));
        const newProfessions = professionOptions.filter(p => filterStateRef.current.profession_id?.includes(p.value));
        const newPlans = planOptions.filter(p => filterStateRef.current.plan_id?.includes(p.value));
        setSelectedCities(newCities);
        setSelectedProfessions(newProfessions);
        setSelectedPlans(newPlans);

        // Sync Verification Status quick filter
        setInlineVerification(filterStateRef.current.verification_status || "all");

        // Sync Created Date quick filter
        setCreatedDateRange({
          start: filterStateRef.current.start_date || null,
          end: filterStateRef.current.end_date || null
        });

        // Sync Last Login Date Range (Activity custom range)
        setLastLoginRange({
          start: filterStateRef.current.last_login_start || null,
          end: filterStateRef.current.last_login_end || null
        });

        setCurrentPage(1);
        closeFilterDrower();
      },
    });
  };

  const handleEditClick = async (item) => {
    setEditLoadingId(item.id);
    try {
      await dispatch(fetchUserDetail(item.id)).unwrap();
      setActiveModal("edit");
    } catch {
      toast.error("Failed to load user details");
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleUpsertUserProfile = (data) => {
    if (!userDetail?.id) return;
    dispatch(updateUserDetails({ id: userDetail.id, data }))
      .unwrap()
      .then(() => {
        toast.success("Profile updated successfully");
        setActiveModal(null);
      })
      .catch((err) => toast.error(err?.message || "Failed to update profile"));
  };

  const handleAddUser = (data) => {
    dispatch(addUser(data))
      .unwrap()
      .then(() => {
        toast.success("User added successfully");
        setActiveModal(null);
        dispatch(fetchAppUserStats());
      })
      .catch((err) => toast.error(err?.message || "Failed to add user"));
  };

  const actionList = [
    {
      title: "View",
      handler: (item) => {
        navigate(`/user/user-detail/${item?.id}`, { state: { from: location.pathname } });
      }
    },
    {
      title: (item) => editLoadingId === item?.id ? "Loading..." : "Edit",
      isVisible: () => hasPermission(MODULE, "update"),
      handler: (item) => handleEditClick(item)
    },
    {
      title: (item) => item?.status === "active" ? "Inactive" : "Active",
      isVisible: () => hasPermission(MODULE, "update"),
      handler: (item) => {
        const nextStatus = item?.status === "active" ? "inactive" : "active";
        confirmChange(item, nextStatus);
      }
    },
    {
      title: "Change Password",
      isVisible: () => hasPermission(MODULE, "update"),
      handler: (item) => {
        setSelectedUser(item);
        setShowPasswordModal(true);
      }
    },
    {
      title: "Delete",
      isVisible: () => hasPermission(MODULE, "delete"),
      handler: (item) => {
        setSelectedUserForDelete(item);
        setShowDeleteModal(true);
      }
    }
  ];

  const columns = [
    {
      id: "name",
      header: "NAME",
      cell: (_, item) => (
        <div className="d-flex align-items-center gap-2">
          <UserInital name={`${item?.first_name || ""} ${item?.last_name || ""}`} image={item?.profile?.profile_photo?.url} />
          <div className="d-flex align-items-center gap-1">
            <span>
              {item?.first_name
                ? `${item.first_name} ${item?.last_name || ""}`.trim()
                : "-"}
            </span>
            {item?.identity_verified && (
              <VerifiedIcon size={16} className="text-success ms-1" />
            )}
            {item?.is_star && (
              <Star size={16} className="ms-1 text-warning" />
            )}
          </div>
        </div>
      )
    },
    {
      id: "mobile_number",
      header: "MOBILE NUMBER",
      cell: (_, item) => item?.mobile_number ? item.mobile_number : "-"
    },
    {
      id: "email",
      header: "EMAIL ADDRESS",
      cell: (_, item) => item?.email ? item.email : ""
    },
    {
      id: "profession",
      header: "PROFESSION",
      cell: (_, item) => item?.profession ? item.profession.name : "-"
    },
    {
      id: "city",
      header: "CITY",
      cell: (_, item) => item?.city ? item.city.name : "-"
    },
    {
      id: "connections",
      header: "CONNECTIONS",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => item?.connection_count ? item.connection_count : "0"
    },
    {
      id: "followers",
      header: "FOLLOWERS",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => item?.followers_count ? item.followers_count : "0"
    },
    {
      id: "posts",
      header: "POSTS",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => item?.post_count ? item.post_count : "0"
    },
    {
      id: "companies",
      header: "COMPANIES",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => item?.company_count ? item.company_count : "0"
    },
    {
      id: "verification_status",
      header: "VERIFICATION STATUS",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => (
        <StatusChip
          bg={item?.verification_status === "verified" ? "success" : "warning"}
          pill
          title={item?.verification_status === "verified" ? "Verified" : "Pending"}
        />
      )
    },
    {
      id: "subscription_plan",
      header: "SUBSCRIPTION PLAN",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => (
        <StatusChip
          bg={item?.subscription_plan_name ? "primary" : "light text-secondary border"}
          pill
          title={item?.subscription_plan_name || "Free"}
        />
      )
    },
    {
      id: "is_migrated",
      header: "IS MIGRATED",
      className: "text-center",
      headerClassName: "text-center",
      cell: (_, item) => (
        <StatusChip
          bg={item?.is_migrated ? "success" : "light text-secondary border"}
          pill
          title={item?.is_migrated ? "Yes" : "No"}
        />
      )
    },
    {
      id: "status",
      header: "STATUS",
      cell: (_, item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown>
            <StatusChip
              bg={
                item?.status === "active"
                  ? "success"
                  : item?.status === "inactive"
                    ? "danger"
                    : "warning"
              }
              pill
              title={
                item?.status === "active"
                  ? "Active"
                  : item?.status === "inactive"
                    ? "Inactive"
                    : "Blocked"
              }
              onClick={(e) => {
                if (hasPermission(MODULE, "update")) {
                  handleStatusChipClick(e, item);
                }
              }}
            />

            {hasPermission(MODULE, "update") && (
              <Dropdown.Menu style={{ zIndex: 9999 }}>
                {item?.status !== "active" && (
                  <Dropdown.Item as="button" onClick={() => confirmChange(item, "active")}>
                    Active
                  </Dropdown.Item>
                )}

                {item?.status !== "inactive" && (
                  <Dropdown.Item as="button" onClick={() => confirmChange(item, "inactive")}>
                    Inactive
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            )}
          </Dropdown>
        </div>
      )
    },
    {
      id: "last_login_at",
      header: "LAST LOGIN AT",
      cell: (_, item) => item?.last_login_at ? formatMomentDate(item.last_login_at, "DD/MM/YYYY, hh:mm A") : "-"
    },
    {
      id: "last_active_at",
      header: "LAST ACTIVE AT",
      cell: (_, item) => item?.last_active_at ? formatMomentDate(item.last_active_at, "DD/MM/YYYY, hh:mm A") : "-"
    },
    {
      id: "created_at",
      header: "CREATED AT",
      cell: (_, item) => item?.created_at ? formatMomentDate(item.created_at, "DD/MM/YYYY, hh:mm A") : "-"
    }
  ];

  const pagination = {
    currentPage,
    totalPages: Math.ceil((data?.pagination?.total || 0) / (limit?.value || 10)),
    total: data?.pagination?.total || 0,
    onPageChange: (page) => setCurrentPage(page),
    pageSize: limit?.value || 10,
    setPageSize: (newLimit) => handleChange({ value: newLimit, label: newLimit }),
    setCurrentPage,
    perPageOptions: [10, 20, 50, 100],
  };
  const confirmChange = async (item, status) => {
    setIsUpdatingStatus(true);
    try {
      const result = await dispatch(
        changeUserStatus({
          id: item.id,
          status: status,
        })
      ).unwrap();

      if (result?.success) {
        toast.success(result?.message || "User status updated successfully");

        // Local state is already updated by changeUserStatus.fulfilled reducer in the slice.
        // Refresh stats after a short delay to ensure backend counts are accurate.
        setTimeout(() => {
          dispatch(fetchAppUserStats());
        }, 1200);

        setShowStatusModal(false);
      }
    } catch (err) {
      console.error("Status update failed:", err);
      toast.error("Failed to update user status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDelete = () => {
    if (!selectedUserForDelete) return;

    dispatch(deleteAppUser(selectedUserForDelete.id))
      .unwrap()
      .then(() => {
        toast.success("User deleted successfully");
        setShowDeleteModal(false);
        setSelectedUserForDelete(null);
        dispatch(fetchAppUserStats());
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to delete user");
      });
  };

  const handleStatusChipClick = (e, item) => {
    e.stopPropagation();
    setSelectedUserForStatus(item);
    setShowStatusModal(true);
  };

  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        <span className="text-muted fw-normal">User Management / </span>User List
      </h4>
      <div className="action-buttons-container d-flex align-items-center gap-3">
        {hasPermission(MODULE, "export") && (
          <CommonButton
            icon={<Upload04Icon strokeWidth="2.0" />}
            variant="secondary"
            className=""
            title={isExporting ? "Exporting..." : "Export"}
            type="button"
            disabled={isExporting}
            isLoading={isExporting}
            onClick={async () => {
              setIsExporting(true);
              try {
                await dispatch(exportAllUsers({
                  search: searchTerm,
                  status: cardStatus,
                  city_id: selectedCities.map((c) => c.value),
                  profession_id: selectedProfessions.map((p) => p.value),
                })).unwrap();
              } catch (err) {
                console.error("Export failed:", err);
              } finally {
                setIsExporting(false);
              }
            }}
          />
        )}
        {hasPermission(MODULE, "create") && (
          <CommonButton
            variant="primary"
            title="Add User"
            icon={<Plus size={16} />}
            type="button"
            onClick={() => setActiveModal("add")}
          />
        )}
      </div>
    </div>
  );

  const getActivityLabel = () => {
    if (advancedFilters?.active_today) return "Active Today";
    if (advancedFilters?.active_last_7_days) return "Active (Last 7 Days)";
    if (advancedFilters?.inactive_days === "7") return "Inactive 7+ Days";
    if (advancedFilters?.inactive_days === "10") return "Inactive 10+ Days";
    if (advancedFilters?.inactive_days === "30") return "Inactive 30+ Days";
    if (advancedFilters?.inactive_days === "90") return "Inactive 90+ Days";
    if (advancedFilters?.inactive_days === "180") return "Dormant Users (180+ Days)";
    if (advancedFilters?.never_logged_in) return "Never Logged In";
    if (advancedFilters?.last_login_start || advancedFilters?.last_login_end) return "Custom Range";
    return "All Activity";
  };

  const handleActivityChange = (type, val = null) => {
    setAdvancedFilters(prev => ({
      ...prev,
      active_today: type === 'active_today' ? true : null,
      active_last_7_days: type === 'active_last_7_days' ? true : null,
      inactive_days: type === 'inactive_days' ? val : "",
      never_logged_in: type === 'never_logged_in' ? true : null,
      last_login_start: type === 'custom_range' ? prev.last_login_start : null,
      last_login_end: type === 'custom_range' ? prev.last_login_end : null,
    }));

    if (type !== 'custom_range') {
      setLastLoginRange({ start: null, end: null });
      setCurrentPage(1);
    }
  };
  const activityOptions = [
    { value: 'all', label: 'All Activity', icon: null },
    { value: 'active_today', label: 'Active Today', icon: <Clock size={14} className="text-primary" /> },
    { value: 'active_last_7_days', label: 'Active (Last 7 Days)', icon: <Clock size={14} className="text-success" /> },
    { value: 'inactive_days', day: '7', label: 'Inactive 7+ Days', icon: <RecordIcon size={14} className="text-warning" /> },
    { value: 'inactive_days', day: '10', label: 'Inactive 10+ Days', icon: <RecordIcon size={14} className="text-warning" /> },
    {
      value: 'inactive_days',
      day: '30',
      label: 'Inactive 30+ Days',
      icon: <RecordIcon size={10} className="text-danger" />,
    },
    {
      value: 'inactive_days',
      day: '90',
      label: 'Inactive 90+ Days',
      icon: <RecordIcon size={10} className="text-danger" />
    },
    {
      value: 'inactive_days',
      day: '180',
      label: 'Dormant Users (180+ Days)',
      icon: <RecordIcon size={10} className="text-danger" />
    },
    {
      value: 'never_logged_in',
      label: 'Never Logged In',
      icon: <EyeOff size={14} className="text-danger" />

    },
  ];

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
      {/* table fiter section */}
      <div className="p-3 d-flex flex-wrap align-items-end column-gap-3 row-gap-2 bg-white ">
        {/* Search */}
        <TableSearch
          value={searchTerm}
          placeholder="Search by name, mobile, email..."
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }} onClear={() => {
            setSearchTerm("");
            setCurrentPage(1);
          }} />

        {/* Verification Status */}
        <CustomSelect
          label={{ name: "Verification Status", class: "mb-0 fs-xs" }}
          options={[
            { value: "all", label: "All" },
            { value: "verified", label: "Verified" },
            { value: "pending", label: "Not Verified" }
          ]}
          value={inlineVerification}
          isClearable={false}
          onChange={(selected) => {
            const val = selected ? selected.value : "all";
            setInlineVerification(val);
            setAdvancedFilters(prev => ({ ...prev, verification_status: val === "all" ? "" : val }));
            setCurrentPage(1);
          }}
          style={{ width: '150px' }}
        />

        {/* City */}

        <CustomSelect
          options={mergedCityOptions}
          value={selectedCities}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          selectProps={{
            components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
            styles: customStyles
          }}
          placeholder="All"
          onInputChange={handleCitySearch}
          onChange={(selected) => {
            const selectedArr = selected || [];
            setSelectedCities(selectedArr);
            setAdvancedFilters(prev => ({ ...prev, city_id: selectedArr.map(s => s.value) }));
            setCurrentPage(1);
          }}
          label={{ name: "City", class: "mb-0 fs-xs" }}
          style={{ width: "150px" }}
        />



        {/* Profession */}

        <CustomSelect
          options={mergedProfessionOptions}
          value={selectedProfessions}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          selectProps={{
            components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
            styles: customStyles
          }}
          placeholder="All"
          onInputChange={handleProfessionSearch}
          onChange={(selected) => {
            const selectedArr = selected || [];
            setSelectedProfessions(selectedArr);
            setAdvancedFilters(prev => ({ ...prev, profession_id: selectedArr.map(s => s.value) }));
            setCurrentPage(1);
          }}
          label={{ name: "Profession", class: "mb-0 fs-xs" }}
          style={{ width: "160px" }}
        />


        {/* Subscription Plan */}

        <CustomSelect
          options={planOptions}
          value={selectedPlans}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          selectProps={{
            components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
            styles: customStyles
          }}
          placeholder="All"
          onChange={(selected) => {
            const selectedArr = selected || [];
            setSelectedPlans(selectedArr);
            setAdvancedFilters(prev => ({ ...prev, plan_id: selectedArr.map(s => s.value) }));
            setCurrentPage(1);
          }}
          label={{ name: "Subscription Plan", class: "mb-0 fs-xs" }}
          style={{ width: "170px" }}
        />


        {/* Created Date */}
        <InputField
          style={{ width: '260px' }}
          label={{ name: "Created Date", class: "mb-0 fs-xs" }}
          input={{
            type: "date-range",
            value: createdDateRange,
            placeholder: "Select date range",
            onChange: (e) => {
              const val = e.target.value;
              setCreatedDateRange({ start: val?.start || null, end: val?.end || null });
              setAdvancedFilters(prev => ({
                ...prev,
                start_date: val?.start || null,
                end_date: val?.end || null,
              }));
              setCurrentPage(1);
            },
          }}
        />

        {/* Activity Dropdown */}
        <div>
          <FormLabel labelName="Activity" className="mb-0 fs-xs" />
          <Dropdown className="floating-label-group" style={{ width: '180px' }}>

            <Dropdown.Toggle variant="select" className="text-truncate" id="dropdown-activity">
              {getActivityLabel()}
            </Dropdown.Toggle>

            <Dropdown.Menu className="w-100 shadow-sm border-0 py-2">
              {activityOptions?.map((item, idx) => (
                <Dropdown.Item as={"button"} key={idx} onClick={() => handleActivityChange(item?.value, item?.day)} className="d-flex align-items-center gap-2 fs-sm">
                  {item?.icon}
                  {item?.label}
                </Dropdown.Item>
              ))}
              <Dropdown.Divider className="my-1" />
              <Dropdown.Item as={"button"} onClick={(e) => e.stopPropagation()} className="text-primary position-relative" >
                <Calendar size={16} className="me-2 align-middle lh-base" />
                Custom Range
                <div className="opacity-0 position-absolute top-0 bottom-0 start-0 end-0">
                  <CustomDateRange
                    startDate={lastLoginRange.start}
                    endDate={lastLoginRange.end}
                    onApply={(start, end) => {
                      setLastLoginRange({ start, end });
                      setAdvancedFilters(prev => ({
                        ...prev,
                        active_today: null,
                        active_last_7_days: null,
                        inactive_days: "",
                        never_logged_in: null,
                        last_login_start: start,
                        last_login_end: end
                      }));
                      setCurrentPage(1);
                    }}
                    onClear={() => {
                      setLastLoginRange({ start: null, end: null });
                      handleActivityChange('all');
                    }}
                    label="Custom Range"
                  />
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>


        {/* Buttons */}
        <div className="d-flex align-items-center gap-2 ms-auto filter-buttons">
          {!isFilterActive && !searchTerm ? null : <ResetBtn onClick={handleResetFilters} />}
          <FilterBtn onClick={handleOpenFilterDrawer} active={isFilterActive} count={activeFilterCount} />
        </div>
      </div>

      <CustomTable
        data={data?.data || []}
        columns={columns}
        loading={isLoading && !isInitialLoad}
        emptyMessage="No users found"
        keyField="id"
        pagination={pagination}
        actionList={actionList}
        rowClick={(row) =>
          navigate(`/user/user-detail/${row?.id}`, { state: { from: location.pathname } })
        }
      />

      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={(newStatus) => confirmChange(selectedUserForStatus, newStatus)}
        currentStatus={selectedUserForStatus?.status}
        isLoading={isUpdatingStatus}
        title="Change Status"
        size="md"
      />

      {selectedUser && (
        <PasswordChangeModal
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSubmit={async (newPassword) => {
            const result = await dispatch(changeUserPassword({ id: selectedUser.id, password: newPassword }));
            if (changeUserPassword.rejected.match(result)) {
              throw new Error(result.payload?.message || "Failed to change password");
            }
          }}
          title={`Change Password for ${selectedUser?.first_name} ${selectedUser?.last_name}`}
        />
      )}

      <DeletePopup
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        content={`Are you sure you want to delete ${selectedUserForDelete?.first_name || 'this user'}? This will anonymize their profile and soft-delete their account.`}
        saveTitle="Delete"
        onConfirm={handleDelete}
      />

      <EditProfileModal
        isOpen={activeModal !== null}
        onClose={() => setActiveModal(null)}
        onSubmit={activeModal === "edit" ? handleUpsertUserProfile : handleAddUser}
        user={activeModal === "edit" ? userDetail : undefined}
        isEdit={activeModal === "edit"}
        isLoading={updateUserLoading}
        title={activeModal === "edit" ? "Edit Profile" : "Add New User"}
        size="lg"
      />
    </>
  );
};

export default ListSection;
